import clsx from 'clsx';
import React, { useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Column } from 'react-table';
import { toTitleCase } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import Datatable from '../../../../../modules/components/datatable/Datatable';
import Header from '../../../../../modules/components/datatable/Header';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ListLoading from '../../../../../modules/components/loading/ListLoading';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';
import { DataSiftField, SiService, siteIntelligenceServiceLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { AddWebContent } from '../form/AddWebContent';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import { getFieldUpdate, getTranslatedValueWithFallback, SiRequestUser } from '../utils';

interface Sort {
  name: string;
  sort: 1 | -1;
}

interface Props {
  data: any;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
}

export default function MccDetail({
  data,
  translation,
  currentLanguage,
  refetch,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();
  const t = (id: string) => intl.formatMessage({ id: `SI_MCC_DETAIL.${id}` });
  const emDash = '—';
  const results = data;
  const detectedMcc = useMemo(() => {
    if (!Array.isArray(results?.result?.detected_mcc)) {
      return [];
    }

    return getTranslatedValueWithFallback(
      translation,
      currentLanguage,
      'company_information.detected_mcc',
      results.result.detected_mcc
    );
  }, [results, currentLanguage]);

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
      >
        <div className='p-9 vstack gap-9'>
          {detectedMcc.length > 0 ? (
            <div className='vstack gap-4'>
              {detectedMcc.map((item: any, index: number) => (
                <div key={`${item?._id ?? item?.code ?? 'mcc'}-${index}`} className='border border-gray-300 rounded-4 p-5'>
                  <div className='fw-bolder fs-5 text-gray-900 mb-1'>
                    {t('MCC_CODE')} {item?.code ?? emDash}
                  </div>

                  <div className='text-gray-700 fs-6 mb-2'>
                    {item?.mastercard_title || item?.visa_title || emDash}
                  </div>

                  <div className='text-gray-700 fs-7 fst-italic'>
                    {item?.reason ? `"${item.reason}"` : emDash}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-gray-600 fs-6'>{emDash}</div>
          )}
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>
    </>
  );
}
